// src/services/emailService.ts
// Service untuk mengirim email otomatis menggunakan EmailJS

import emailjs from '@emailjs/browser';

// Konfigurasi EmailJS
const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'your_service_id';
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'your_template_id';
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'your_public_key';

// Interface untuk data kontak
export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}

// Interface untuk template email
interface EmailTemplate {
  to_name: string;
  from_name: string;
  from_email: string;
  phone: string;
  subject: string;
  message: string;
  reply_to: string;
  [key: string]: unknown;
}

// Inisialisasi EmailJS
export const initEmailJS = () => {
  try {
    if (EMAILJS_PUBLIC_KEY && EMAILJS_PUBLIC_KEY !== 'your_public_key') {
      emailjs.init(EMAILJS_PUBLIC_KEY);
      console.log('EmailJS initialized successfully');
    } else {
      console.warn('EmailJS not configured - please set environment variables');
    }
  } catch (error) {
    console.error('Failed to initialize EmailJS:', error);
  }
};

// Fungsi untuk mengirim email notifikasi
export const sendContactNotification = async (contactData: ContactFormData): Promise<boolean> => {
  try {
    // Validasi konfigurasi
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      console.warn('EmailJS configuration missing. Please set environment variables.');
      return false;
    }

    // Template data untuk email
    const templateParams: EmailTemplate = {
      to_name: 'Admin', // Nama penerima email
      from_name: contactData.name,
      from_email: contactData.email,
      phone: contactData.phone || 'Tidak disediakan',
      subject: contactData.subject,
      message: contactData.message,
      reply_to: contactData.email
    };

    // Kirim email menggunakan EmailJS
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    );

    if (response.status === 200) {
      console.log('Email notification sent successfully:', response);
      return true;
    } else {
      console.error('Failed to send email notification:', response);
      return false;
    }
  } catch (error) {
    console.error('Error sending email notification:', error);
    return false;
  }
};

// Fungsi untuk mengirim email konfirmasi ke pengirim
export const sendConfirmationEmail = async (contactData: ContactFormData): Promise<boolean> => {
  try {
    // Template untuk email konfirmasi
    const confirmationTemplate = {
      to_name: contactData.name,
      to_email: contactData.email,
      subject: contactData.subject,
      message: `Terima kasih telah menghubungi kami. Pesan Anda dengan subjek "${contactData.subject}" telah kami terima dan akan segera ditindaklanjuti dalam 24 jam.`
    };

    // Kirim email konfirmasi (gunakan template terpisah jika ada)
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      'confirmation_template', // Template ID untuk konfirmasi
      confirmationTemplate
    );

    if (response.status === 200) {
      console.log('Confirmation email sent successfully');
      return true;
    } else {
      console.error('Failed to send confirmation email');
      return false;
    }
  } catch (error) {
    console.error('Error sending confirmation email:', error);
    return false;
  }
};

// Fungsi utama untuk menangani pengiriman email
export const handleContactEmailNotification = async (contactData: ContactFormData): Promise<{
  notificationSent: boolean;
  confirmationSent: boolean;
}> => {
  const results = {
    notificationSent: false,
    confirmationSent: false
  };

  // Check if EmailJS is properly configured
  if (!EMAILJS_SERVICE_ID || EMAILJS_SERVICE_ID === 'your_service_id' ||
      !EMAILJS_TEMPLATE_ID || EMAILJS_TEMPLATE_ID === 'your_template_id' ||
      !EMAILJS_PUBLIC_KEY || EMAILJS_PUBLIC_KEY === 'your_public_key') {
    console.warn('EmailJS not configured - please set environment variables');
    return results;
  }

  try {
    // Kirim email notifikasi ke admin
    results.notificationSent = await sendContactNotification(contactData);
    
    // Kirim email konfirmasi ke pengirim (opsional)
    // results.confirmationSent = await sendConfirmationEmail(contactData);
    
    return results;
  } catch (error) {
    console.error('Error in email notification process:', error);
    return results;
  }
};

export default {
  initEmailJS,
  sendContactNotification,
  sendConfirmationEmail,
  handleContactEmailNotification
};
